import { useState, useCallback, useEffect } from 'react';
import {
  EditOutlined,
  KeyOutlined,
  PlusCircleOutlined,
  RedoOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useToast } from '../../hooks/toast';
import { useNavigate } from 'react-router-dom';
import { IUser } from '../../shared/interfaces';
import { formatDate } from '../../shared/utils/format-date';
import ResetPassword from './ResetPassword';
import {
  ATable,
  DropdownOptions,
  MenuOptions,
  PageIntro,
  PageLayout,
  PagePanel,
  StatusActive,
} from '../../components';
import { getUsers } from '../../http';
import { useAuth } from '../../hooks/auth';
import { PermissionActions } from '../../shared/enums/permissions.enum';
import { useI18n } from '../../shared/i18n';

const Users: React.FC = () => {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { permissionFind } = useAuth();
  const { t } = useI18n();
  const [search, setSearch] = useState<string>('');
  const [users, setUsers] = useState<IUser[]>([]);
  const [user, setUser] = useState<IUser | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();
  const [modalReset, setModalReset] = useState<boolean>(false);

  const list = useCallback(async () => {
    setLoading(true);
    setUsers([]);
    try {
      const response = await getUsers(limit, (page - 1) * limit, search);
      setPages(response.count);
      setUsers(response.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: t('usersPage.toast.errorTitle'),
        description: t('usersPage.toast.listError'),
      });
    } finally {
      setLoading(false);
    }
  }, [limit, search, page, addToast, t]);

  useEffect(() => {
    list();
  }, [page, search, limit, list]);

  return (
    <PageLayout>
      <PageIntro
        badge={t('usersPage.intro.badge')}
        icon={<UserOutlined />}
        title={t('usersPage.intro.listTitle')}
        description={t('usersPage.intro.listDescription')}
      />
      <MenuOptions
        menu_items={[
          {
            label: t('usersPage.actions.refresh'),
            key: '1',
            icon: <RedoOutlined />,
            onClick: () => list(),
          },
          permissionFind([PermissionActions.usersCreate])
            ? {
                label: t('usersPage.actions.create'),
                key: '2',
                icon: <PlusCircleOutlined />,
                onClick: () => navigate(`/users/new`),
              }
            : null,
        ]}
        showInputSearch
        search={search}
        setSearch={setSearch}
      />
      <PagePanel>
        <ATable
          columns={[
            {
              title: t('usersPage.table.cpf'),
              dataIndex: 'cpf',
            },
            {
              title: t('usersPage.table.name'),
              dataIndex: 'name',
            },
            {
              title: t('usersPage.table.birthDate'),
              dataIndex: 'birthday',
              render: (value: string) => <>{formatDate(value)}</>,
            },
            {
              title: t('usersPage.table.login'),
              dataIndex: 'email',
            },
            {
              title: t('usersPage.table.status'),
              dataIndex: 'status',
              width: 95,
              render: (value: boolean) => <StatusActive status={value} />,
            },
            {
              title: t('usersPage.table.options'),
              width: 130,
              render: (cell: IUser) => (
                <DropdownOptions
                  items={[
                    permissionFind([PermissionActions.usersUpdate])
                      ? {
                          label: t('usersPage.actions.edit'),
                          key: '1',
                          icon: <EditOutlined />,
                          onClick: () => navigate(`/users/${cell.id}`),
                        }
                      : null,
                    permissionFind([PermissionActions.usersResetPassword])
                      ? {
                          label: t('usersPage.actions.resetPassword'),
                          key: '2',
                          icon: <KeyOutlined />,
                          onClick: () => {
                            setUser(cell);
                            setModalReset(true);
                          },
                        }
                      : null,
                  ]}
                />
              ),
            },
          ]}
          loading={loading}
          dataSource={users}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
      {modalReset && (
        <ResetPassword
          open={modalReset}
          close={() => setModalReset(false)}
          userEdited={user}
        />
      )}
    </PageLayout>
  );
};

export default Users;
